/**
 * Building a strategy, and editing one — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE FORM FOR BOTH, BECAUSE THEY ARE THE SAME FORM
 *
 * A separate "new strategy" dialog would be this file with the fields blank,
 * and the two would drift: a criterion added to one, a bound tightened in the
 * other. `strategy === null` is the only difference, and it decides the title,
 * whether Delete is offered, and which endpoint the save goes to.
 *
 * WHAT IS STILL DELIBERATELY NOT HERE
 *
 * Stop loss, target and trailing. `StrategyLeg` reserves columns for them and
 * Phase 7's guardian is what will honour them. A field the form can write and
 * nothing enforces is not an unfinished feature, it is a lie that costs money
 * — the trader would size a position believing a stop exists.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useMemo, useState } from 'react'
import { ListPlus, Plus, Trash2, X } from 'lucide-react'
import {
  EXPIRY_KINDS,
  MAX_STRATEGY_LEGS,
  STRATEGY_BIASES,
  type ExpiryKind,
} from '@apna/shared'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChecklistEditor } from '@/screens/terminal/ChecklistEditor'
import type { StrategyCreate, StrategyDto, StrategyPatch } from '@/types/strategy'
import { BIAS_LABEL, EXPIRY_LABEL } from '../constants'
import {
  copyLeg,
  draftFromLeg,
  duplicateLegs,
  legsOf,
  newLeg,
  usesDelta,
  type LegDraft,
} from '../utils/legDraft'
import { LegHeader, LegRow } from './LegRow'

const FIELD_LABEL = 'text-[10px] font-medium uppercase tracking-[0.07em] text-muted-foreground'

export function StrategyEditor({
  strategy,
  saving,
  deleting,
  onSave,
  onCreate,
  onDelete,
  onClose,
}: {
  /** Null is a strategy that does not exist yet. */
  strategy: StrategyDto | null
  saving: boolean
  deleting?: boolean
  onSave: (patch: StrategyPatch) => void
  onCreate: (spec: StrategyCreate) => void
  onDelete?: () => void
  onClose: () => void
}) {
  const creating = strategy === null

  const [name, setName] = useState(strategy?.name ?? '')
  const [bias, setBias] = useState(strategy?.bias ?? 'NEUTRAL')
  const [expiryKind, setExpiryKind] = useState(strategy?.expiryKind ?? 'WEEKLY')
  /*
   * LOTS LIVE ON THE LEG, and the strategy's own `defaultLots` is derived
   * from them on save — see `decomposeLots`. A trader sizing a ratio spread
   * says "1 and 2", not "size 1, ratios 1 and 2".
   */
  const [drafts, setDrafts] = useState<LegDraft[]>(() =>
    strategy ? strategy.legs.map((l) => draftFromLeg(l, strategy.defaultLots)) : [newLeg()],
  )
  const [error, setError] = useState<string | null>(null)
  const [badLeg, setBadLeg] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  /*
   * THE CHECKLIST IS PART OF THIS FORM'S SAVE, not a separate immediate
   * write like the one in the trade confirmation. There, an edit happens
   * seconds before an order and has to stick on its own; here the trader is
   * configuring, and Cancel must undo everything they typed.
   *
   * `source` is what the patch sends: 'inherited' omits the field entirely,
   * so a save that did not touch the checklist cannot silently adopt
   * today's default as the trader's own copy.
   */
  const [checks, setChecks] = useState<string[]>(() => [...(strategy?.checklist ?? [])])
  const [source, setSource] = useState<'inherited' | 'custom'>(
    strategy?.checklistCustomised ? 'custom' : 'inherited',
  )

  const dupes = useMemo(() => new Set(duplicateLegs(drafts)), [drafts])
  const usingDelta = useMemo(() => usesDelta(drafts), [drafts])

  /*
   * The cap bites on GROWTH, matching the server. A strategy already past it
   * — the thirty-leg speed test — stays editable and may shrink; it simply
   * cannot gain a leg. A flat rule would make it unopenable in the only
   * screen that could cut it down.
   */
  const ceiling = Math.max(MAX_STRATEGY_LEGS, strategy?.legs.length ?? 0)
  const canAdd = drafts.length < ceiling

  const setLeg = (key: string, patch: Partial<LegDraft>) =>
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)))

  const addLeg = () => setDrafts((ds) => [...ds, newLeg(ds.at(-1))])
  const removeLeg = (key: string) => setDrafts((ds) => ds.filter((d) => d.key !== key))
  /** Inserted straight after its original, where the eye already is. */
  const duplicateLeg = (key: string) =>
    setDrafts((ds) => {
      const i = ds.findIndex((d) => d.key === key)
      if (i < 0) return ds
      return [...ds.slice(0, i + 1), copyLeg(ds[i]!), ...ds.slice(i + 1)]
    })

  const editChecks = (next: string[]): void => {
    setChecks(next)
    setSource('custom')
  }

  function submit() {
    setBadLeg(null)

    const trimmed = name.trim()
    if (creating && trimmed === '') {
      setError('Give the strategy a name.')
      return
    }

    const legs = legsOf(drafts)
    if (!legs.ok) {
      setError(legs.why)
      setBadLeg(legs.at ?? null)
      return
    }
    const n = legs.defaultLots ?? 1

    if (checks.length > 20) {
      setError('A strategy holds at most 20 rules.')
      return
    }

    setError(null)

    if (creating) {
      onCreate({
        name: trimmed,
        bias,
        defaultLots: n,
        expiryKind,
        legs: legs.value,
        ...(source === 'custom' ? { checklist: checks } : {}),
      })
      return
    }

    // Legs go WHOLE. The list IS the new set — see `legSpecSchema`.
    onSave({
      defaultLots: n,
      expiryKind,
      legs: legs.value,
      ...(trimmed && trimmed !== strategy.name ? { name: trimmed } : {}),
      ...(source === 'custom' ? { checklist: checks } : {}),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="qbar-card flex max-h-[90vh] w-full max-w-[1000px] flex-col overflow-hidden rounded-[12px]"
        data-testid="strategy-editor"
      >
        <header className="flex items-center gap-3 border-b border-border px-5 py-3.5">
          <h2 className="text-[15px] font-semibold">
            {creating ? 'New strategy' : 'Edit strategy'}
          </h2>
          <span className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* ── What the strategy is ──────────────────────────────────── */}
          <div className="grid grid-cols-[minmax(0,1.6fr)_148px_148px] gap-3">
            <label className="flex flex-col gap-1.5">
              <span className={FIELD_LABEL}>Name</span>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Monday condor"
                data-testid="edit-name"
                maxLength={60}
              />
            </label>

            <div className="flex flex-col gap-1.5">
              <span className={FIELD_LABEL}>Bias</span>
              {/*
                Fixed once a strategy exists. Bias is what the page groups and
                filters by, and a trader who moved their condor under BULLISH
                would be filing it where they will not look for it. Building a
                new one is where that choice belongs.
              */}
              <Select value={bias} onValueChange={setBias} disabled={!creating}>
                <SelectTrigger
                  className="h-9 text-[13px]"
                  data-testid="edit-bias"
                  aria-label="Bias"
                  title={
                    creating
                      ? undefined
                      : 'Set when the strategy was created. The page groups and filters by it, so moving one is how a trader loses track of it.'
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STRATEGY_BIASES.map((b) => (
                    <SelectItem key={b} value={b}>
                      {BIAS_LABEL[b] ?? b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={FIELD_LABEL}>Expiry</span>
              {/* Every leg trades this unless it names one of its own. */}
              <Select value={expiryKind} onValueChange={setExpiryKind}>
                <SelectTrigger className="h-9 text-[13px]" data-testid="edit-expiry" aria-label="Expiry">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_KINDS.map((k: ExpiryKind) => (
                    <SelectItem key={k} value={k}>
                      {EXPIRY_LABEL[k] ?? k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── The legs ──────────────────────────────────────────────── */}
          <section className="mt-5">
            <div className="mb-1.5 flex items-baseline gap-2">
              <h3 className="text-[12.5px] font-semibold">Legs</h3>
              <span className="num text-[11px] text-muted-foreground" data-testid="leg-count">
                {drafts.length} of {ceiling}
              </span>
            </div>

            <div className="overflow-x-auto">
              <div className="min-w-[840px]">
                <LegHeader />
                <div className="flex flex-col gap-1">
                  {drafts.map((d, i) => (
                    <LegRow
                      key={d.key}
                      draft={d}
                      index={i}
                      invalid={badLeg === i}
                      duplicate={dupes.has(i)}
                      canRemove={drafts.length > 1}
                      canCopy={canAdd}
                      onChange={(patch) => setLeg(d.key, patch)}
                      onCopy={() => duplicateLeg(d.key)}
                      onRemove={() => removeLeg(d.key)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-2 flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={addLeg}
                disabled={!canAdd}
                data-testid="add-leg"
              >
                <Plus size={12} />
                Add leg
              </Button>
              {!canAdd && (
                <span className="text-[11.5px] text-muted-foreground">
                  {ceiling} legs is the most a strategy can hold.
                </span>
              )}
              {dupes.size > 0 && (
                <span className="text-[11.5px] text-warning" data-testid="duplicate-warning">
                  Two legs are identical — they will place the same order twice.
                </span>
              )}
            </div>

            {usingDelta && (
              /*
               * Said once, where it is actionable. Delta is solved from live
               * prices, so a delta leg cannot resolve on a dead chain — and
               * discovering that at the moment of firing is the refusal this
               * line exists to move earlier.
               */
              <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="delta-note">
                Delta legs need live prices to pick their strike.
              </p>
            )}
          </section>

          {/* ── The gate before it fires ──────────────────────────────── */}
          <section className="mt-5 border-t border-border pt-4" data-testid="editor-checklist">
            <div className="mb-2 flex items-baseline gap-2">
              <h3 className="text-[12.5px] font-semibold">Rules</h3>
              <span className="num text-[11px] text-muted-foreground" data-testid="rule-count">
                {checks.length === 0 ? 'none' : `${checks.length} of 20`}
              </span>
            </div>
            {/*
              Narrower than the legs above it on purpose. A rule is a short
              sentence read under time pressure, and a full-width box invites
              a paragraph — which is the one length nobody reads before an
              order.
            */}
            <ChecklistEditor
              items={checks}
              onChange={editChecks}
              className="max-w-[520px]"
              placeholder="Spot inside the expected range…"
              addLabel="Add rule"
              addIcon={<ListPlus size={13} />}
            />
          </section>
        </div>

        <footer className="flex items-center gap-2 border-t border-border px-5 py-3">
          {!creating && strategy.isCustom && onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => (confirmDelete ? onDelete() : setConfirmDelete(true))}
              disabled={deleting}
              data-testid="delete-strategy"
              className={cn(
                'text-muted-foreground hover:text-destructive',
                confirmDelete && 'text-destructive',
              )}
            >
              <Trash2 size={13} />
              {confirmDelete ? 'Delete for good?' : 'Delete'}
            </Button>
          )}

          {error ? (
            <span className="flex-1 text-[12px] text-destructive" data-testid="edit-error">
              {error}
            </span>
          ) : (
            <span className="flex-1" />
          )}

          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={saving} data-testid="edit-save">
            {saving ? 'Saving…' : creating ? 'Create' : 'Save'}
          </Button>
        </footer>
      </div>
    </div>
  )
}
